import type { ReportDraftFileInput } from "~/services/aiOrchestrator";

import parseFileToText from "~/helpers/aiChat/parseFileToText";
import setupFileRules from "~/helpers/aiChat/setupFiles";

export type ReportDraftFileType = "image" | "pdf" | "spreadsheet";

export interface ReportDraftFileInfo {
	mediaType: string;
	type: ReportDraftFileType;
}

interface ChatFileAcceptState {
	acceptsSetupFiles: boolean;
	isDraftOffered: boolean;
	isDraftStarted: boolean;
}

const FILE_TYPES: Record<string, ReportDraftFileInfo> = {
	csv: { mediaType: "text/csv", type: "spreadsheet" },
	jpeg: { mediaType: "image/jpeg", type: "image" },
	jpg: { mediaType: "image/jpeg", type: "image" },
	pdf: { mediaType: "application/pdf", type: "pdf" },
	png: { mediaType: "image/png", type: "image" },
	webp: { mediaType: "image/webp", type: "image" },
	xls: { mediaType: "text/csv", type: "spreadsheet" },
	xlsx: { mediaType: "text/csv", type: "spreadsheet" }
};

const REPORT_DRAFT_ACCEPT = Object.keys(FILE_TYPES)
	.map((extension) => `.${extension}`)
	.join(",");

const CHAT_FILE_ACCEPT = ".csv,.tsv,.txt,.xlsx,.xls";

const BASE64_CHUNK_BYTES = 0x8000;

const draftFileInfo = (name: string): ReportDraftFileInfo | null => {
	const dot = name.lastIndexOf(".");
	if (dot < 0) return null;
	return FILE_TYPES[name.slice(dot + 1).toLowerCase()] ?? null;
};

const isChatDraftFile = (name: string): boolean => {
	const type = draftFileInfo(name)?.type;
	return type === "pdf" || type === "image";
};

const chatFileAccept = ({ acceptsSetupFiles, isDraftOffered, isDraftStarted }: ChatFileAcceptState): string => {
	if (isDraftStarted) return REPORT_DRAFT_ACCEPT;
	if (acceptsSetupFiles) return setupFileRules.SETUP_FILE_ACCEPT;
	return isDraftOffered ? `${CHAT_FILE_ACCEPT},${REPORT_DRAFT_ACCEPT}` : CHAT_FILE_ACCEPT;
};

const readBase64 = async (file: File): Promise<string> => {
	const bytes = new Uint8Array(await file.arrayBuffer());
	let binary = "";
	for (let start = 0; start < bytes.length; start += BASE64_CHUNK_BYTES) {
		binary += String.fromCharCode(...bytes.subarray(start, start + BASE64_CHUNK_BYTES));
	}
	return btoa(binary);
};

const readDraftFile = async (file: File, info: ReportDraftFileInfo): Promise<ReportDraftFileInput> =>
	info.type === "spreadsheet"
		? { mediaType: info.mediaType, name: file.name, text: await parseFileToText(file) }
		: { contentBase64: await readBase64(file), mediaType: info.mediaType, name: file.name };

const reportDraftFiles = {
	chatFileAccept,
	draftFileInfo,
	isChatDraftFile,
	readDraftFile,
	REPORT_DRAFT_ACCEPT
};

export default reportDraftFiles;
